import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { Project } from '@/api/types';

type ProjectContextValue = {
  projects: Project[];
  projectsLoading: boolean;
  selectedProjectId: string | null;
  setSelectedProjectId: (id: string | null) => void;
  selectedProject: Project | null;
};

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(urlProject);

  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiFetch<{ projects: Project[] }>('/api/projects'),
  });

  const projects = useMemo(() => data?.projects ?? [], [data?.projects]);

  const setSelectedProjectId = useCallback(
    (id: string | null) => {
      setSelectedProjectIdState(id);
      const next = new URLSearchParams(searchParams);
      if (id) next.set('project', id);
      else next.delete('project');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectedProject = useMemo(() => {
    if (!selectedProjectId) return null;
    return projects.find((p) => p.id === selectedProjectId) ?? null;
  }, [projects, selectedProjectId]);

  const value = useMemo(
    () => ({
      projects,
      projectsLoading: isLoading,
      selectedProjectId,
      setSelectedProjectId,
      selectedProject,
    }),
    [projects, isLoading, selectedProjectId, selectedProject, setSelectedProjectId],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProjectContext(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjectContext binnen ProjectProvider');
  return ctx;
}
